import { useAuthStore } from '@client/stores/AuthStore';
import { queryClient } from '@client/trpc/client';
import { isProtectedRoute } from '@client/trpc/sessionHandling';
import { identifyAnonymous } from '@client/utils/feature-flags';
import { debug } from '@client/utils/logger';
import { armLogoutRedirectFallback, expireSession } from './sessionExpiry';

const TOKEN_SYNC_CHANNEL = 'curium-auth-sync';
const DEBUG = false; // Set to true to enable debug logging

interface TokenSyncMessage {
    type: 'token-updated' | 'token-expired' | 'logout' | 'org-changed';
    token?: string;
    orgId?: string;
    timestamp: number;
    tabId: string;
}

/**
 * Simplified cross-tab token synchronization using BroadcastChannel API
 * BroadcastChannel is supported in 96%+ of browsers (all modern browsers)
 */
class CrossTabTokenSync {
    private broadcastChannel: BroadcastChannel | null = null;
    private tabId: string;
    private isProcessingSyncUpdate = false;
    private isInitialized = false;

    constructor() {
        // Generate unique tab ID to prevent self-echo
        this.tabId = `tab-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

        // Initialize immediately if in browser environment
        if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
            this.initializeSync();
        }
    }

    private debug(message: string, data?: any) {
        if (DEBUG) {
            console.log(`[CrossTabSync ${this.tabId}] ${message}`, data || '');
        }
    }

    private initializeSync() {
        try {
            if (this.isInitialized) {
                return;
            }

            this.broadcastChannel = new BroadcastChannel(TOKEN_SYNC_CHANNEL);
            this.broadcastChannel.addEventListener('message', this.handleMessage);
            this.isInitialized = true;
            this.debug('BroadcastChannel initialized');
        } catch (error) {
            console.error('Failed to initialize cross-tab sync:', error);
        }
    }

    private handleMessage = (event: MessageEvent<TokenSyncMessage>) => {
        this.handleSyncMessage(event.data);
    };

    private handleSyncMessage = async (message: TokenSyncMessage) => {
        try {
            // Ignore our own messages
            if (message.tabId === this.tabId) {
                return;
            }

            // Ignore messages older than 5 seconds
            if (Date.now() - message.timestamp > 5000) {
                return;
            }

            const authStore = useAuthStore.getState();

            // Set flag to prevent infinite loop when updating token
            this.isProcessingSyncUpdate = true;

            switch (message.type) {
                case 'token-updated':
                    // Tokens are identity-only after the refactor; an updated token cannot encode
                    // an org change. Org switches are broadcast explicitly via 'org-changed'.
                    if (message.token && message.token !== authStore.getAuthToken()) {
                        authStore.setAuthToken(message.token);
                    }
                    break;

                case 'token-expired': {
                    // BroadcastChannel messages are origin-wide, so anonymous public tabs
                    // (e.g. /password-reset, /invite, /public/*) also receive this
                    // event. Only enter expired-session mode when this tab is still on a
                    // protected route or still carries an auth token; otherwise ignore the
                    // signal so anonymous tRPC traffic is not cancelled.
                    const onProtectedRoute = isProtectedRoute(window.location.pathname);
                    const hasToken = authStore.isTokenSet();

                    if (onProtectedRoute || hasToken) {
                        expireSession(
                            '[CrossTabTokenSync] Remote token-expired sync received, clearing auth state and cancelling active queries',
                        );
                    } else {
                        debug('[CrossTabTokenSync] Ignoring remote token-expired on anonymous public route');
                    }
                    break;
                }

                case 'logout':
                    debug('[CrossTabTokenSync] Clearing auth token');
                    // Mark this tab as logging out before clearing the token, so background
                    // token-refresh machinery and RequireAuth stand down. Another tab's
                    // logout has already wiped the shared Auth0 cache — a renewal here would
                    // fail with MissingRefreshTokenError and surface a misleading error.
                    useAuthStore.setState({ isLoggingOut: true });
                    identifyAnonymous();
                    authStore.clearAuthToken();
                    window.location.href = '/login';
                    // A dirty form's "Leave site?" prompt answered with Stay cancels this
                    // navigation without an error; bound the window (see armLogoutRedirectFallback).
                    armLogoutRedirectFallback();
                    break;

                case 'org-changed': {
                    // BroadcastChannel messages are origin-wide, so anonymous tabs (e.g.
                    // /public/* forms, /invite) and auth-transition pages also receive this
                    // event. The /org-switched interstitial only exists to stop org-scoped
                    // authenticated views from mixing data across orgs — a public form is keyed
                    // off the orgId in its own URL, not authStore.org, so there is nothing to
                    // protect outside a protected route. Without this guard a signed-in user's
                    // org persisted in localStorage is still present on the public tab, so the
                    // check below fires there too and the interstitial hijacks the public form.
                    // Unlike token-expired this deliberately does not fall back to hasToken: the
                    // persisted token is precisely what produces the false positive.
                    if (!isProtectedRoute(window.location.pathname)) {
                        debug('[CrossTabTokenSync] Ignoring remote org-changed outside protected route');
                        break;
                    }
                    if (message.orgId !== undefined) {
                        const currentOrg = authStore.org;
                        const messageOrgId = BigInt(message.orgId);

                        if (currentOrg && currentOrg.id !== messageOrgId) {
                            sessionStorage.setItem(
                                'org-switch-info',
                                JSON.stringify({
                                    fromOrgId: currentOrg.id.toString(),
                                    fromOrgName: currentOrg.name,
                                    toOrgId: message.orgId,
                                    timestamp: Date.now(),
                                }),
                            );
                            // Clear cached queries before navigation. In-flight queries against
                            // the old org would otherwise resolve and write to cache before the
                            // hard navigation cancels them, briefly leaking the previous org's
                            // data. Mirrors AuthStore.changeOrg().
                            queryClient.clear();
                            window.location.href = '/org-switched';
                        }
                    }
                    break;
                }
            }
        } catch (error) {
            console.error('Error handling sync message:', error);
        } finally {
            // Always reset the flag, even on error
            this.isProcessingSyncUpdate = false;
        }
    };

    public broadcastTokenUpdate(token: string) {
        // Don't broadcast if we're processing a sync update to prevent loops
        if (this.isProcessingSyncUpdate) {
            return;
        }

        const message: TokenSyncMessage = {
            type: 'token-updated',
            token,
            timestamp: Date.now(),
            tabId: this.tabId,
        };
        this.broadcast(message);
    }

    public broadcastTokenExpired() {
        // Suppress rebroadcast when we are already applying a remote sync update.
        if (this.isProcessingSyncUpdate) {
            return;
        }

        const message: TokenSyncMessage = {
            type: 'token-expired',
            timestamp: Date.now(),
            tabId: this.tabId,
        };
        this.broadcast(message);
    }

    public broadcastLogout() {
        const message: TokenSyncMessage = {
            type: 'logout',
            timestamp: Date.now(),
            tabId: this.tabId,
        };
        this.broadcast(message);
    }

    public broadcastOrgChange(token: string, orgId: bigint) {
        const message: TokenSyncMessage = {
            type: 'org-changed',
            token,
            orgId: orgId.toString(),
            timestamp: Date.now(),
            tabId: this.tabId,
        };
        this.broadcast(message);
    }

    private broadcast(message: TokenSyncMessage) {
        try {
            if (!this.broadcastChannel) {
                this.debug('BroadcastChannel not available, skipping broadcast');
                return;
            }

            this.broadcastChannel.postMessage(message);
            this.debug(`Broadcasted ${message.type}`, message);
        } catch (error) {
            console.error('Failed to broadcast sync message:', error);
        }
    }

    public destroy() {
        try {
            if (this.broadcastChannel) {
                this.broadcastChannel.removeEventListener('message', this.handleMessage);
                this.broadcastChannel.close();
                this.broadcastChannel = null;
            }
            this.isInitialized = false;
            this.debug('CrossTabSync destroyed');
        } catch (error) {
            console.error('Error during cross-tab sync cleanup:', error);
        }
    }
}

// Export singleton instance
export const crossTabTokenSync = new CrossTabTokenSync();
